/**
 * Team Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { bumpHubEntityVersion } from '@/lib/reference-sync/bump-hub-entity.js';
import { useTeamStore } from '@/store/team.store';
import { useTenantStore } from '@/store/tenant.store';
import { Logger } from '@/lib/app-log.js';

export class TeamIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch teams and update store
     */
    async fetchTeams(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useTeamStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            // With createBaseController, response.data (payload) has:
            // { success, data (array), pagination, uniqueValues }
            const { data, pagination, uniqueValues } = response;
            store.setTeams(data || []);

            // Map pagination from backend response payload
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                // Fallback if pagination missing
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch teams for dropdown
     */
    async fetchTeamsForDropdown(options = {}) {
        const store = useTeamStore.getState();
        const requestTenantId = options.tenantId || useTenantStore.getState().selectedTenantId;
        try {
            const result = await fetchDropdownOptionsFromReference('team', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            }, options);
            if (String(useTenantStore.getState().selectedTenantId || '') !== String(requestTenantId || '')) return;
            store.setTeamsForDropdown(result.data || []);
        } catch (error) {
            Logger.error('Failed to fetch team dropdown', { error: error?.message });
        }
    }

    /**
     * Create team
     */
    async createTeam(data) {
        const store = useTeamStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('generalSystemTeam:messages.createSuccess'));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update team
     */
    async updateTeam(id, data) {
        const store = useTeamStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('generalSystemTeam:messages.updateSuccess'));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete team
     */
    async deleteTeam(id) {
        const store = useTeamStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('generalSystemTeam:messages.deleteSuccess'));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete teams
     * @param {Array<string>} ids
     */
    async bulkDeleteTeams(ids) {
        const store = useTeamStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('generalSystemTeam:messages.bulkDeleteSuccess', { count: ids.length }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchTeamById(id, { persist = true } = {}) {
        const store = useTeamStore.getState();
        if (persist) store.setSelectedTeamLoading(true);
        store.clearError();
        try {
            const response = await this.service.getById(id);
            if (persist) store.setSelectedTeam(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            if (persist) store.setSelectedTeam(null);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (persist) store.setSelectedTeamLoading(false);
        }
    }

    async getAssignments(id, params = {}) {
        try {
            const response = await this.service.getAssignments(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            return { success: false, data: [], pagination: {}, error: this._getErrorMessage(error) };
        }
    }

    async createAssignment(id, body, opts = {}) {
        try {
            const response = await this.service.createAssignment(id, body);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.linkSuccess', { defaultValue: 'Teams linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateAssignment(id, assignmentId, assignment, opts = {}) {
        try {
            const body = Array.isArray(assignment) ? { members: assignment } : assignment;
            const response = await this.service.updateAssignment(id, assignmentId, body);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.updateSuccess', { defaultValue: 'Assignment updated successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteAssignment(id, assignmentId, opts = {}) {
        try {
            const response = await this.service.deleteAssignment(id, assignmentId);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.unlinkSuccess', { defaultValue: 'Team unlinked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getDutyProfiles(id, params = {}) {
        try {
            const response = await this.service.getDutyProfiles(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            return { success: false, data: [], pagination: {}, error: this._getErrorMessage(error) };
        }
    }

    async createDutyProfile(id, body, opts = {}) {
        try {
            const response = await this.service.createDutyProfile(id, body);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.profileCreateSuccess', { defaultValue: 'Duty profile created successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateDutyProfile(id, profileId, body, opts = {}) {
        try {
            const response = await this.service.updateDutyProfile(id, profileId, body);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.profileUpdateSuccess', { defaultValue: 'Duty profile updated successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteDutyProfile(id, profileId, opts = {}) {
        try {
            const response = await this.service.deleteDutyProfile(id, profileId);
            bumpHubEntityVersion('team');
            if (!opts.silent) {
                toast.success(i18next.t('teamAssociation:messages.profileDeleteSuccess', { defaultValue: 'Duty profile deleted successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundKey: 'generalSystemTeam:messages.notFoundError',
        });
    }
}
